import React, {useEffect, useState} from 'react'
import {useInView} from 'react-intersection-observer'
import {
  Alerts,
  If,
  ReportEntryCard,
  RPCDocs,
  usePrevious,
  useRPCContext,
  useRPCServerEventHandler,
} from '@therms/react-modules'
import {Button, Loading} from '@therms/atalaya'
import {SelectEntryTypeModal} from '@src/modules/Reports/components/ViewReport/components/SelectEntryTypeModal'
import {EntryFormModal} from '@src/modules/Reports/components/ViewReport/components/EntryFormModal'
import {SingleEntryForm} from '@src/modules/Reports/components/ViewReport/components/SingleEntryForm'
import {SingleEntryDisplay} from '@src/modules/Reports/components/ViewReport/components/SingleEntryDisplay'
import {useSetState} from 'ahooks'
import {useCurrentUserAnPCheck} from '@src/modules/Users/shared/hooks/useCurrentUserAnPCheck'
import Sort = RPCDocs.Reports.GetReportEntryList.v2.Sort

interface ReportInputProps {
  formBuilderValidation?: {[key: string]: string} | undefined
  onFormBuilderValidate?: (errors?: {[key: string]: string}) => void
  onUnsavedChanges?: (hasUnsavedChanges: boolean) => void
  report: RPCDocs.Reports.GetReport.v1.Report
  reportType: RPCDocs.Reports.GetReport.v1.ReportTypeType
}

function ReportInput({
  formBuilderValidation,
  onFormBuilderValidate,
  onUnsavedChanges,
  report,
  reportType,
}: ReportInputProps) {
  const {rpcService} = useRPCContext()

  const previousReportId = usePrevious(report.id)
  const [data, setData] = useState<RPCDocs.Reports.GetReportEntryList.v2.Data>()
  const [loading, setLoading] = useState(true)
  const [entriesPaginationLimit, setEntriesPaginationLimit] = useState(25)

  const canEdit = useCurrentUserAnPCheck(
    (user) =>
      report?.authors?.map((a) => a.id)?.includes(user.id) ||
      user.anp.role.ops.permissions.contributeToOtherUserReports ||
      user.anp.role.ops.permissions.editOtherUserReports,
  )

  const [state, setState] = useSetState({
    editEntryId: '',
    entryTypeId: '',
    showSelectEntry: false,
  })

  // when the Report is "open" then show most recent at the top
  const sortDirection = report.state === 'open' ? Sort.Desc : Sort.Asc

  const loadReportEntries = () => {
    setLoading(true)

    rpcService.Reports.GetReportEntryList.v2
      .call({
        pagination: {limit: entriesPaginationLimit},
        reportId: report.id,
        sort: ['timestamp', sortDirection],
      })
      .then((res) => setData(res.data))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (previousReportId !== report.id) {
      setData(undefined)
      setEntriesPaginationLimit(25)
    }

    loadReportEntries()
  }, [entriesPaginationLimit, report.id, sortDirection])

  const {ref: infiniteScrollRef} = useInView({
    onChange: (inView) => {
      if (inView && !loading) {
        if (entriesPaginationLimit < (data?.total || 0)) {
          setEntriesPaginationLimit(entriesPaginationLimit + 25)
        }
      }
    },
    /* Optional options */
    threshold: 0,
    triggerOnce: false,
  })

  useRPCServerEventHandler('ReportEntriesUpdated', (event, payload) => {
    if (report.id === payload.reportId) {
      Alerts.info(
        <div>
          Report entries updated{' '}
          <Button onClick={loadReportEntries} subtle variant="main">
            Reload
          </Button>
        </div>,
        {autoClose: 3000, toastId: 'report-entries-updated'},
      )
    }
  })

  const reportIsEditable = !report.deleted && report.state === 'open' && canEdit

  if (reportType.inputConfig.type === 'single-entry') {
    if (!data?.entries?.length && loading) return <Loading overlay />

    if (!reportType.inputConfig.entryTypeIds[0]) {
      return (
        <div className="text-color-critical text-center">
          Report Type is mis-configured, no Form Builder information found
        </div>
      )
    }

    if (reportIsEditable) {
      return (
        <SingleEntryForm
          entryId={data?.entries?.[0]?.id}
          entryTypeId={reportType.inputConfig.entryTypeIds[0]}
          onFormBuilderValidate={onFormBuilderValidate}
          onSaved={() => {
            if (!data?.entries?.[0]?.id) {
              loadReportEntries()
            }
          }}
          onUnsavedChanges={onUnsavedChanges}
          reportId={report.id}
        />
      )
    }

    if (data?.entries?.length) {
      return (
        <SingleEntryDisplay
          entryId={data?.entries[0]?.id}
          reportId={report.id}
        />
      )
    }

    // this is the case when the current User does not have permission to edit and there is no entry to display
    return (
      <div className="p-xl text-color-neutral text-center">
        <em>No information to display</em>
      </div>
    )
  }

  const mountInfiniteScroll = (data?.entries?.length || 0) < (data?.total || 0)

  return (
    <div>
      <If truthy={reportIsEditable}>
        <div className="mb-5 inline-flex justify-between">
          <div>
            <Button
              onClick={() => setState({showSelectEntry: true})}
              variant="main"
            >
              Add New Entry
            </Button>
          </div>
        </div>
      </If>

      <div className="gap-y-base relative flex min-h-[200px] flex-col">
        {data?.entries?.map((entry) => (
          <ReportEntryCard
            entry={entry}
            key={entry.id}
            onEdit={
              reportIsEditable && canEdit
                ? () => {
                    setState({
                      editEntryId: entry.id,
                      entryTypeId: entry.entryType.id,
                    })
                  }
                : undefined
            }
          />
        ))}

        {mountInfiniteScroll && (
          <div className="h-20" ref={infiniteScrollRef} />
        )}

        {loading && (
          <div className="relative min-h-[100px] w-full">
            <Loading message="Loading Report Entries" overlay size="xl" />
          </div>
        )}
      </div>

      {state.showSelectEntry && (
        <SelectEntryTypeModal
          closeHandler={() => setState({showSelectEntry: false})}
          entryTypeIds={reportType.inputConfig.entryTypeIds}
          onSelectEntryType={(entryTypeId) => {
            setState({
              entryTypeId,
              showSelectEntry: false,
            })
          }}
        />
      )}

      {(state.entryTypeId || state.editEntryId) && (
        <EntryFormModal
          entryId={state.editEntryId}
          entryTypeId={state.entryTypeId}
          formBuilderValidation={formBuilderValidation}
          onComplete={() => {
            setState({
              editEntryId: '',
              entryTypeId: '',
              showSelectEntry: false,
            })

            loadReportEntries()
          }}
          onFormBuilderValidate={onFormBuilderValidate}
          closeHandler={() =>
            setState({
              editEntryId: '',
              entryTypeId: '',
              showSelectEntry: false,
            })
          }
        />
      )}
    </div>
  )
}

export {ReportInput}
